
import { DatePipe, NgClass, NgStyle } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { BodyLayoutComponent } from '@reflact/ngx-layout';
import { getContrastColorSafe } from '@shared-angular';
import { AdminService } from '@shared-angular/services/admin.service';
import { AdminLayoutService } from '@shared-angular/services/angular-layout.service';
import { CounterService } from '@shared-angular/services/counter.service';
import { GlobalLoadingService } from '@shared-angular/services/global-loading.service';
import { LanguageService } from '@shared-angular/services/language.service';
import { LoginService } from '@shared-angular/services/login.service';
import { McpService } from '@shared-angular/services/mcp.service';
import { ChatbotConfig, OutputTriggerTypeArr, ParserTypeArr, TriggerActionArr } from '@shared/index';
import dayjs from 'dayjs';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PopoverModule } from 'primeng/popover';
import { filter, Subscription } from 'rxjs';
import { ChatbotEmbedComponent } from 'src/app/chatbot-embed/chatbot-embed.component';
import { capitalizeOrParse } from 'src/app/routes/ai-bot/admin/tabs/general/configurations-tab/fusionbot-tab/fusionbot-tab.utils';
import { SupervisorService } from 'src/app/routes/ai-bot/admin/tabs/insights/supervisor-tab/supervisor.service';
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';
import { FileUploadService } from 'src/app/shared/services/file-upload.service';
import { ProjectService } from 'src/app/shared/services/project.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';

import { FusionService } from '@shared-angular/services/fusion.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { BadgeModule } from "primeng/badge";
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { MessageModule } from "primeng/message";
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from "primeng/tag";
import { PageHeaderOverEditorComponent } from "src/app/page-header-over-editor/page-header-over-editor.component";
import { EmbedTabComponent } from "src/app/routes/ai-bot/admin/tabs/general/embed-tab/embed-tab.component";
import { TrunkService } from 'src/app/shared/services/trunk.service';
import { UserDocsAdminService } from 'src/app/shared/services/user-docs-admin.service';
import { WebscrapeService } from 'src/app/shared/services/webscrape.service';
import { INavData } from 'src/app/shared/types/nav-data.types';
import { BotService } from '../../../services/bot.service';
import { SkillCreateDialogComponent } from '../skill-create-dialog/skill-create-dialog.component';
import { BotAdminCardBodyContentComponent } from './bot-admin-card-body-content/bot-admin-card-body-content.component';

export type MenuItem = {
  type: 'link';
  text: string;
  href: string;
  hidden?: () => boolean;
  icon: string;
  cssClass?: string;
} | {
  type: 'separator';
  text: string;
  addAction?: () => void;
}

@Component({
  selector: 'app-bot-editor',
  templateUrl: './bot-editor.component.html',
  styleUrls: ['./bot-editor.component.scss'],
  imports: [RouterOutlet, RouterLink, RouterLinkActive, NgClass, NgStyle, DatePipe, ButtonModule,
    DrawerModule,
    ChatbotEmbedComponent,
    FormsModule,
    TabsModule,
    PopoverModule,
    BotAdminCardBodyContentComponent,
    BsDropdownModule,
    NgSelectModule,
    ProgressbarModule,
    TooltipModule,
    BodyLayoutComponent,
    EmbedTabComponent, BlockUIModule, ProgressBarModule,
    PageHeaderOverEditorComponent,
    SkillCreateDialogComponent,
    MessageModule,
    BadgeModule,
    TagModule,
    BlockUIModule
  ]
})
export class BotEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('menuEntry', { static: true }) public menuEntry!: TemplateRef<unknown>;
  @ViewChild('quarantineModal') public quarantineModal!: TemplateRef<unknown>;
  public botConfig!: ChatbotConfig;
  public selectedAdminArea: string = $localize`:@@bot-task:Bot-Aufgabe`;
  public triggerTypes = OutputTriggerTypeArr;
  public triggerActions = TriggerActionArr;
  public triggerParser = ParserTypeArr;

  public separatedTagList: { label: string }[] = [];
  public navItems: INavData[] = [];
  public getContrastColor = getContrastColorSafe;
  public tags: { label: string }[] = [];
  public isScrolled: boolean = false;
  public helpUrls: string[] = [];
  protected botConfigSmall: { name: string, id: string }[] = [];
  protected modalRef?: BsModalRef;
  protected acceptQuarantineRisk: boolean = false;
  protected skipQuarantineToday: boolean = false;
  public showExportPanel = false;
  public showSkillCreateDialog = false;
  public userDocCount: number = 0;


  public adminAreas: MenuItem[] = [
    { type: 'separator', text: $localize`:@@general:Allgemein` },
    { type: 'link', text: $localize`:@@change-history-menu-item:Änderungshistorie`, hidden: (() => !this.rss.botConfig.showChangeHistory), icon: 'ri-information-2-fill', href: "information" },
    { type: 'link', text: $localize`:@@bot-task:Bot-Aufgabe`, icon: 'ri-lightbulb-flash-fill', href: "task" },
    /* { type: 'link', text: $localize`:@@access-control:Zugriff`, icon: 'ri-lock-password-fill', href: "access-control" }, */
    { type: 'link', text: $localize`:@@configuration:Konfiguration`, icon: 'ri-tools-fill', href: "config" },
    { type: 'link', text: $localize`:@@stage-manager:Stage-Manager`, icon: 'ri-stack-fill ps-3', href: "stage-manager" },
    { type: 'link', text: $localize`:@@scripting:Scripting`, icon: 'ri-puzzle-2-fill ps-3', href: "blockly" },
    { type: 'link', text: $localize`:@@fusion-bot:Fusion-View`, icon: 'ri-clapperboard-ai-fill ps-3', href: "fusionbot" },
    { type: 'link', text: $localize`:@@privacy:Datenschutz`, icon: 'ri-shield-user-fill', href: "privacy-policy" },

    { type: 'link', text: $localize`:@@api:API`, icon: 'ri-code-s-slash-fill', href: "externalapi" },
    { type: 'link', text: $localize`:@@changelog:Änderungsverlauf`, icon: 'ri-file-history-fill', href: "changelog" },
    /* { type: 'link', text: $localize`:@@misc:Sonstiges`, icon: 'ri-more-fill', href: "misc" }, */

    { type: 'separator', text: $localize`:@@skills:Skills`, addAction: () => this.openSkillCreateDialog() },
    { type: 'link', text: $localize`:@@if-then:Wenn-Dann`, icon: 'ri-swap-2-fill', href: "if-then", hidden: () => this.rss.getToolsByType("customtoolkit", false).length === 0 },
    { type: 'link', text: $localize`:@@documents:Dokumente`, icon: 'ri-article-fill', href: "documents", hidden: () => this.counter.getFiles(this.rss.botConfig._id) === 0 },
    { type: 'link', text: $localize`:@@user-docs:Nutzer-Dokumente`, icon: 'ri-user-received-fill', href: "user-docs", hidden: () => this.userDocCount === 0 },
    { type: 'link', text: $localize`:@@online-sources:Online-Quellen`, icon: 'ri-cloud-fill', href: "online-sources", hidden: () => this.rss.getToolsByType("websitesearch", false).length === 0 },
    { type: 'link', text: $localize`:@@databases:Datenbanken`, icon: 'ri-database-2-fill', href: "databases", hidden: () => this.rss.datatrunks.length === 0 },
    { type: 'link', text: $localize`:@@offline-websites:Offline-Websites`, icon: 'ri-cloud-off-fill', href: "offline-websites", hidden: () => this.rss.webscrapes.length === 0 },
    { type: 'link', text: $localize`:@@webhooks:Webhooks`, icon: 'ri-webhook-fill', href: "webhooks", hidden: () => this.rss.getToolsByType("webhook", false).length === 0 },
    { type: 'link', text: $localize`:@@mcp-server:MCP-Server`, icon: 'ri-server-fill', href: "mcp-server", hidden: () => this.counter.getMcps(this.rss.botConfig._id) === 0 },
    { type: 'separator', text: $localize`:@@insights:Insights` },
    { type: 'link', text: $localize`:@@memory:Gedächtnis`, icon: 'ri-brain-fill', href: "memory" },
    { type: 'link', text: $localize`:@@supervisor:Supervisor`, icon: 'ri-ghost-fill', href: "supervisor" },
    { type: 'link', text: $localize`:@@feedback:Feedback`, icon: 'ri-feedback-fill', href: "feedback" },
    { type: 'link', text: $localize`:@@statistics:Statistik`, icon: 'ri-bar-chart-fill', href: "statistics" },
    { type: 'link', text: $localize`:@@latency-menu-item:Antwortzeiten`, icon: 'ri-timer-flash-line', href: "latency" },
    { type: 'link', text: $localize`:@@user-statistics:Nutzerstatistik`, icon: 'ri-line-chart-line', href: "userstatistics" },
    { type: 'link', text: $localize`:@@user-values:Datenspeicher`, icon: 'ri-table-line', href: "uservalues" }
  ];
  public capitalizeOrParse = capitalizeOrParse;

  private routerSubscription: Subscription;
  constructor(
    private aRoute: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    public fileUpload: FileUploadService,
    private trunkService: TrunkService,
    public counter: CounterService,
    public loginService: LoginService,
    public langService: LanguageService,
    public rss: RouteShareService,
    public editorItemService: EditorItemsService,
    public wss: WebscrapeService,
    public adminService: AdminService,
    public supervisorService: SupervisorService,
    public layoutService: AdminLayoutService,
    public bot: BotService,
    public loadingService: GlobalLoadingService,
    public fusionViewService: FusionService,
    public projectService: ProjectService,
    public modalSerivce: BsModalService,
    private userDocsAdminService: UserDocsAdminService,
    private mcpService: McpService,
  ) {
    this.routerSubscription = this.router.events
      .pipe(filter(value => value instanceof NavigationEnd))
      .subscribe(() => {
        this.navItems = [];
        this.adminAreasToNavItems(this.adminAreas);
      });

    this.counter.navChange$.subscribe(() => {
      this.navItems = [];
      this.adminAreasToNavItems(this.adminAreas);
    });
  }


  public async ngOnInit(): Promise<void> {

    this.generateHelpUrlArray();

    this.separatedTagList = this.rss.botConfig.tagList.map(tag => ({ label: tag }));

    await Promise.all([
      this.trunkService.getTrunks(this.rss.botConfig._id),
      this.wss.getWebscrapesForBot(this.rss.botConfig._id)
    ]);
    this.supervisorService.joinSupervisorView(this.rss.botConfig._id);

    this.supervisorService.loadHelpRequests();
    this.counter.setTools(this.rss.botConfig._id, [...this.rss.getToolsByType("schnittstelle"), ...this.rss.getToolsByType("websitesearch")].length);
    this.counter.setWennDann(this.rss.botConfig._id, this.rss.getToolsByType("customtoolkit").length);
    this.counter.setWebhooks(this.rss.botConfig._id, this.rss.getToolsByType("webhook").length);
    this.supervisorService.notificationUnsolvedSummary();
    const userDocsResult = await this.userDocsAdminService.getUserDocuments(this.rss.botConfig._id);
    this.userDocCount = userDocsResult.grouped.length;
    this.navItems = [];
    this.adminAreasToNavItems(this.adminAreas);
  }

  public ngAfterViewInit(): void {
    this.adminAreasToNavItems(this.adminAreas);
  }

  public ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    this.supervisorService.leaveSupervisorView();
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    RAGBOT.destroy();
  }

  public menuIsActive(item: INavData): boolean {
    return localStorage.getItem('selectedAdminArea') === item.name;
  }

  public logout(): void {
    void this.loginService.logout(true);
  }

  public changeSelectedBot(botId: string) {
    const secondSegment = this.aRoute.snapshot.firstChild?.url[0]?.path;
    if (secondSegment) {
      void this.router.navigate(['/bot-admin', botId, secondSegment]);
    } else {
      void this.router.navigate(['/bot-admin', botId]);
    }
  }





  public adminAreasToNavItems(areas: MenuItem[]): INavData[] {
    if (this.navItems.length > 0) {
      return this.navItems;
    }
    areas.forEach((area: MenuItem) => {
      const newItem = {} as INavData & { hidden: boolean };
      if (area.type == "separator") {
        if (area.addAction) {
          // Kombiniertes Item: Library ignoriert template bei title:true → non-title mit name+addAction
          this.navItems.push({ name: area.text, template: this.menuEntry, addAction: area.addAction });
          return;
        }
        newItem.title = true;
        newItem.name = area.text;
        newItem.template = this.menuEntry;
      }
      if (area.type == "link") {
        newItem.href = area.href;
        newItem.name = area.text;
        newItem.template = this.menuEntry;
        newItem.hidden = area.hidden ? area.hidden() : false;
        newItem.icon = area.icon;
      }
      if (!newItem.hidden) {
        this.navItems.push(newItem);
      }
    });
    return this.navItems;
  }

  public async saveTagsToBotConfig() {
    this.rss.botConfig.tagList = this.separatedTagList.map((tag: {
      label: string;
    }) => tag.label);
    this.separatedTagList = this.rss.botConfig.tagList.map(tag => ({ label: tag }));
    await this.rss.saveBotConfigField('tagList');
  }




  public urlFromItem(item: string): string {
    if (!item || item.trim() === '') {
      return '';
    }
    const firstPart = item.split('|')[0];
    return firstPart ? firstPart.trim() : '';


  }
  public nameFromItem(item: string): string {
    if (!item || typeof item !== 'string') {
      return 'Bot';
    }
    const parts = item.trim().split('|');
    if (!parts[1]) {
      return 'Bot';
    }
    return parts.length > 1 ? parts[1].trim() : '';
  }

  public generateHelpUrlArray(): string[] {
    if (this.helpUrls.length > 1) return this.helpUrls;
    const str = this.rss.botConfig.helpUrls.trim();
    let rows = [];
    rows = str.split('\n');
    if (rows.length == 1 && rows[0] === '') {
      rows = ["https://ki-toolbox.reflactportal.com/de/bot/68e60f9c10819bea091eeb1f|Allgemeine Hilfe"];
    }

    this.helpUrls = rows.reverse();
    return this.helpUrls;
  }


  protected openQuarantineModal() {
    this.modalRef = this.modalSerivce.show(this.quarantineModal, { class: "modal-lg" });
  }

  protected async releaseFromQuarantine() {
    if (this.skipQuarantineToday) {
      this.rss.botConfig.skipQuarantineUntil = dayjs().endOf("day").toDate();
      await this.rss.saveBotConfigField("skipQuarantineUntil");
    }
    if (this.rss.botConfig.quarantine) {
      this.rss.botConfig.quarantine.isQuarantined = false;
      await this.rss.saveBotConfigField("quarantine");
    }
    this.modalSerivce.hide();
    this.modalRef = undefined;
    this.acceptQuarantineRisk = false;
    this.skipQuarantineToday = false;
  }

  public openSkillCreateDialog(): void {
    this.showSkillCreateDialog = true;
  }
}
